import { Platform } from "@src/mobile/utils/platform";
import { pseudo } from "@src/modules/session";
import { useCallback } from "react";
import { useDispatch, useStore } from "react-redux";
import { useHistory } from "react-router-dom";
import { type AnyAction } from "redux";
import { type ThunkDispatch } from "redux-thunk";

/**
 * Discriminated union documenting the *intent* of the switch at the call
 * site. The hook itself doesn't read `role` — destination is resolved by
 * `renderStartPage` at `/enter` from the resulting Redux session — but the
 * tagged shape forces callers to think about what they're switching to.
 */
export type SwitchUserTarget =
  | { role: "admin"; userId: string }
  | { role: "worker"; userId: string }
  | { role: "workplace"; userId: string };

export interface UseSwitchUserResult {
  /**
   * Switches the active user (impersonate worker/workplace, or return to
   * admin). Awaits `pseudo()` so route guards never see stale Redux
   * state, then defers destination resolution to `/enter` →
   * `renderStartPage`, which is the codebase's single source of truth
   * for "where does this user type land." Reloads on web only —
   * Capacitor's bridge re-delivers the last `appUrlOpen` deep link on
   * reload, which would navigate to AuthRedirect with an expired magic
   * link token (see `useAccountSwitcher.ts` for the original incident
   * write-up).
   */
  switchUser: (target: SwitchUserTarget) => Promise<void>;
}

interface SessionShape {
  userId: string;
}

/**
 * Centralized "switch active user" hook used by every admin context-switch
 * surface (Omnisearch impersonation, return-to-admin, the redesign
 * account switcher). Encapsulates the await-pseudo → /enter → reload
 * sequence so individual call sites stay one-liners and so the ordering
 * contract has a single test surface.
 *
 * Note: `useReloadPageOnUserSwitch` (src/containers/app/hooks/) ALSO
 * triggers `window.location.reload()` on `userId` changes, but only for
 * a hardcoded list of "unsafe" routes. The explicit reload here is
 * belt-and-suspenders for stale-Redux protection across all routes;
 * future maintainers should not delete one assuming the other covers it.
 */
export function useSwitchUser(): UseSwitchUserResult {
  // Typed thunk dispatch so `await dispatch(pseudo(...))` correctly resolves
  // to the thunk's Promise instead of being typed as the action object.
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, AnyAction>>();
  const history = useHistory();
  const store = useStore<{ session: SessionShape }>();

  const switchUser = useCallback(
    async (target: SwitchUserTarget): Promise<void> => {
      if (!target.userId) {
        return;
      }

      // Pre-`pseudo()` localStorage write is a defensive safety net: if
      // the API call inside `pseudo()` fails, the next session-restore
      // still picks the right user. The thunk also writes this key (see
      // `get` in src/modules/session.ts), so the post-resolve write is a
      // duplicate but the pre-resolve write is not.
      localStorage.setItem("selectedUser", target.userId);

      // Awaiting is the entire point of this hook — see ORB-1847.
      await dispatch(pseudo({ userId: target.userId }));

      // If `pseudo()` failed (network error caught by the thunk),
      // `LOGIN_SUCCESS` never fired and `session.userId` is still the
      // previous value. Skip navigation/reload — the toast from the
      // thunk's `.catch` already informed the user, and reloading
      // would needlessly disrupt the page they were already on.
      const { session } = store.getState();
      if (session.userId !== target.userId) {
        return;
      }

      // Defer destination resolution to `/enter` → `renderStartPage`.
      // This is intentional: route guards see the post-pseudo Redux
      // session (the `await` above guarantees that), so the standard
      // "go home for this session" landing logic resolves correctly.
      history.push("/enter");

      if (!Platform.isMobile) {
        window.location.reload();
      }
    },
    [dispatch, history, store]
  );

  return { switchUser };
}
